import { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';

type UserRole = 'super_admin' | 'school_admin' | 'teacher' | 'student' | 'parent' | 'kid';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, metadata: any) => Promise<{ data: any; error: any }>;
  signOut: () => Promise<void>;
  userRoles: UserRole[];
  primaryRole: UserRole | null;
  resetPasswordRequest: (email: string) => Promise<{ error: any }>;
  updatePassword: (newPassword: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const navigate = useNavigate();

  const fetchUserRoles = async (userId: string) => {
    try {
      // Use the RPC function to get user roles
      const { data, error } = await (supabase.rpc as any)('get_user_roles', { _user_id: userId });
      
      if (!error && Array.isArray(data) && data.length > 0 && data[0] !== null) {
        // Data comes back as an array of arrays, so we flatten it
        const roles = Array.isArray(data[0]) ? data[0] : data;
        setUserRoles(roles as UserRole[]);
      } else {
        setUserRoles([]);
      }
    } catch (error) {
      console.error('Error fetching user roles:', error);
      setUserRoles([]);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Defer role fetching to avoid blocking
        if (session?.user) {
          setTimeout(() => {
            fetchUserRoles(session.user.id);
          }, 0);
        } else {
          setUserRoles([]);
        }
        
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchUserRoles(session.user.id);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string, metadata: any) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: metadata,
      }
    });

    // Parse error types for better UX
    if (error) {
      const structuredError = {
        ...error,
        isRateLimited: error.status === 429 || error.message?.includes('security purposes') || error.message?.includes('Email rate limit'),
        isDuplicateEmail: error.message?.includes('already registered') || error.message?.includes('already exists'),
        isNetworkError: error.message?.includes('network') || error.message?.includes('timeout'),
      };
      return { data, error: structuredError };
    }

    return { data, error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUserRoles([]);
    navigate('/login');
  };

  const resetPasswordRequest = async (email: string) => {
    const redirectUrl = `${window.location.origin}/reset-password`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });
    
    return { error };
  };

  const primaryRole = userRoles.length > 0 ? userRoles[0] : null;

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      signIn,
      signUp,
      signOut,
      userRoles,
      primaryRole,
      resetPasswordRequest,
      updatePassword,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
